'use client'

import { useEffect, useRef } from 'react'
import { AnalyticsEventSchema, sanitizeEvent } from '@mist/contracts/analytics'
import type { AnalyticsEventName, AnalyticsProperties } from '@mist/contracts/analytics'
import type { Attribution } from '@mist/contracts/promotion'
import { getAnonymousId, getSessionId, getTouchIds } from './attribution'

/**
 * Typed analytics dispatch (R-ANA-02, R-ANA-03, Section 13.3).
 *
 * Public-route components call this module before the real PostHog browser SDK
 * may be ready. Events are validated against the centralized MIST contract and
 * buffered until `PostHogClientProvider` installs a sink in the `(site)` route
 * group. `/verify` and `/staff` never import that provider, so replay and SDK
 * initialization stay out of those bundles.
 */
export type AnalyticsRecord = {
  readonly name: AnalyticsEventName
  readonly distinctId: string
  readonly sessionId: string
  readonly properties: AnalyticsProperties
}

export type AnalyticsSink = (record: AnalyticsRecord) => void

declare global {
  interface Window {
    __mistAnalytics?: AnalyticsRecord[]
  }
}

let activeSink: AnalyticsSink | undefined

export function attributionProperties(attribution: Attribution): AnalyticsProperties {
  const touch = getTouchIds()
  const props: Record<string, unknown> = {
    route: attribution.landingPath,
    first_touch_id: touch.firstTouchId,
    current_touch_id: touch.currentTouchId,
  }
  if (attribution.utmCampaign) props['campaign'] = attribution.utmCampaign
  if (attribution.referrerDomain) props['referrer_domain'] = attribution.referrerDomain
  if (attribution.utmSource) props['utm_source'] = attribution.utmSource
  if (attribution.utmMedium) props['utm_medium'] = attribution.utmMedium
  if (attribution.utmCampaign) props['utm_campaign'] = attribution.utmCampaign
  if (attribution.utmContent) props['utm_content'] = attribution.utmContent
  if (attribution.utmTerm) props['utm_term'] = attribution.utmTerm
  if (attribution.approvedAdId) props['approved_ad_id'] = attribution.approvedAdId
  if (attribution.referralCampaignId) props['referral_campaign_id'] = attribution.referralCampaignId
  return sanitizeEvent('mist_landing_viewed', props)
}

export function setAnalyticsSink(sink: AnalyticsSink | undefined): void {
  activeSink = sink
}

export function drainBufferedAnalytics(sink: AnalyticsSink): void {
  if (typeof window === 'undefined') return
  const buffered = window.__mistAnalytics ?? []
  window.__mistAnalytics = []
  for (const record of buffered) sink(record)
}

export function trackEvent(name: AnalyticsEventName, properties: AnalyticsProperties = {}): void {
  if (typeof window === 'undefined') return
  const sanitized = sanitizeEvent(name, properties)
  const record = {
    name,
    distinctId: getAnonymousId(),
    sessionId: getSessionId(),
    properties: sanitized,
  }
  const parsed = AnalyticsEventSchema.safeParse({
    eventName: record.name,
    distinctId: record.distinctId,
    properties: record.properties,
  })
  if (!parsed.success) {
    if (process.env.NODE_ENV !== 'production') console.warn('[analytics] dropped non-privacy-safe event', name, parsed.error.issues)
    return
  }
  if (activeSink) {
    activeSink(record)
    return
  }
  window.__mistAnalytics ??= []
  window.__mistAnalytics.push(record)
}

export function trackApiError(route: string, error: { code: string; retryable: boolean; requestId?: string; fieldErrors?: ReadonlyArray<{ code: string }> }): void {
  trackEvent('mist_api_error', {
    route,
    error_code: error.code,
    retryable: error.retryable,
    ...(error.requestId ? { request_id: error.requestId } : {}),
    ...(error.fieldErrors?.length ? { field_error_codes: error.fieldErrors.map((field) => field.code) } : {}),
  })
}

/** Fire an event exactly once when the component mounts. */
export function useTrackOnce(name: AnalyticsEventName, properties: AnalyticsProperties = {}): void {
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current) return
    fired.current = true
    trackEvent(name, properties)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
